'use client';

import type React from 'react';

import { useState } from 'react';
import { Button } from '@heroui/button';
import { Input, Textarea } from '@heroui/input';
// import { Textarea } from "@heroui/textarea";
// import { label } from "@heroui/label";
import { Mail, Phone, MapPin, Send, ArrowRight } from 'lucide-react';
import Link from 'next/link';

import SocialMediaIcons from '@/components/social-icon';

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log('Form submitted:', formData);
    // Reset form after submission
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              Contact Us
            </h1>

            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              We'd love to hear from you! Whether you have a question, need assistance, or want to
              learn more about becoming a PayStop agent, our team is here to help.
            </p>
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container container-md ">
          <div className="grid gap-12 md:grid-cols-2">
            <div className="space-y-8">
              <h2 className="text-3xl font-semibold">Get in Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 mt-1 text-primary" />
                  <div>
                    <h3 className="text-xl font-semibold">Email Us</h3>
                    <p className="text-foreground/70">support@paystop.io</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 mt-1 text-primary" />
                  <div>
                    <h3 className="text-xl font-semibold">Call Us</h3>
                    <p className="text-foreground/70">+237 676962723</p>
                    <p className="text-sm text-foreground/50">
                      (Available Monday to Friday, 9:00 AM to 5:00 PM)
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 mt-1 text-primary" />
                  <div>
                    <h3 className="text-xl font-semibold">Visit Us</h3>
                    <p className="text-foreground/70">
                      Crystal Finance Commercial Avenue Bamenda, P.O. Box 168 Bamenda Cameroon
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-8">
              <h2 className="text-3xl font-semibold">Follow Us</h2>
              <p className="text-foreground/70">
                Stay connected with us through our social media platforms for updates, news, and
                helpful tips:
              </p>
              <div className="flex space-x-4">
                <SocialMediaIcons />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className=" section-md">
        <div className="container container-md">
          {/* Send Us a Message Section */}
          <div className="space-y-8">
            <h2 className="text-3xl font-semibold text-center">Send Us a Message</h2>
            <p className="max-w-3xl mx-auto text-center text-foreground/70">
              If you have specific questions or want more information, you can send us a message
              directly below. We'll get back to you as soon as possible!
            </p>
            <form className="max-w-2xl mx-auto space-y-6" onSubmit={handleSubmit}>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Input
                    required
                    id="name"
                    label="Name"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Input
                    required
                    id="email"
                    label="Email"
                    name="email"
                    placeholder="your@email.com"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div>
                <Input
                  required
                  id="subject"
                  label="Subject"
                  name="subject"
                  placeholder="What's this about?"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Textarea
                  required
                  id="message"
                  label="Message"
                  name="message"
                  placeholder="Your message here..."
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </div>
              <Button className="w-full" color="primary" type="submit">
                Send Message
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>
      {/* Become a PayStop Agent Section */}
      <section className="section-md">
        <div className="container space-y-24 container-max">
          {/* Call to Action */}
          <div className="relative px-8 py-16 overflow-hidden bg-gradient-to-b from-primary/20 to-background/5 rounded-3xl">
            <div className="relative z-10 space-y-4 text-center">
              <h2 className="font-semibold title">Become a PayStop Agent</h2>

              <p className="max-w-4xl mx-auto text-foreground/70">
                If you're interested in becoming an agent or learning more about our business
                opportunities, simply fill out the form above or contact us directly through the
                details provided. We are always looking for motivated individuals and businesses to
                join our network.
              </p>
              <div>
                {' '}
                <Link href="/become-an-agent">
                  <Button color="primary" size="lg">
                    Learn More
                    <ArrowRight className="ml-2 " size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
